// 倉庫番ゲーム メインページ
//
// 設計概要（仕様詳細は ../SPEC.md 参照）:
// - Ray cast は ./ray.ts に集約（common 準拠の下向き ray、distance で物理判定）
// - 各アクション handler は ./actions/ 配下の hook に分離（useHandleForward / useHandleJump / useHandleTurn）
// - index.tsx は scene 初期化、UI レンダリング、hooks 呼び出しのみ

import "@babylonjs/loaders/glTF/2.0/index";
import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Scene } from "@babylonjs/core/scene";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import I18n from "common/i18n";
import Constants from "common/constant";
import { setupGameScene, loadMap, loadMesh } from "common/model";
import { ConfigContext } from "features/top";
import { BackToCourseTop } from "features/courses/components";
import { useCanvas, useGameBgm, useGameSound } from "features/courses/hook";
import { isButton } from "./ray";
import { Success } from "./Success";
import { Tutorial } from "./Tutorial";
import { getSokobanMap } from "../maps";
import { SceneJoystick } from "./SceneJoystick";
import { performCameraIntro } from "../animations";
import { SOKOBAN_LEVELS } from "../level-select/levels";
import { SokobanController } from "./SokobanController";
import { GameState, ROTATION_Y } from "./actions/types";
import { useHandleJump } from "./actions/useHandleJump";
import { useHandleTurn } from "./actions/useHandleTurn";
import { useHandleForward } from "./actions/useHandleForward";

export const SokobanGamePage = () => {
  const { levelId } = useParams<{ levelId?: string }>();
  const navigate = useNavigate();
  const id = Number(levelId);

  const elementRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene | null>(null);
  const stateRef = useRef<GameState | null>(null);
  // アニメ速度倍率。デフォルト 2 倍速。画面側からスピードを動的に変更するため ref で保持。
  const speedRef = useRef<number>(2);
  const [animating, setAnimating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // BGM/効果音
  const configContext = useOutletContext<ConfigContext>();
  const { gameStandByBgm } = useGameBgm();
  const {
    moveSound,
    jumpSound,
    turnSound,
    fallingSound,
    failureSound,
    levelclearSound,
    levelclearCheersSound,
  } = useGameSound();

  const isValidId = !Number.isNaN(id) && id >= 1 && id <= SOKOBAN_LEVELS.length;
  const map = isValidId ? getSokobanMap(id) : null;

  // ----- ゲーム専用 BGM -----
  useEffect(() => {
    if (configContext?.bgmSound && gameStandByBgm) {
      gameStandByBgm.play();
      configContext.bgmSound.stop();
    }
    return () => {
      if (configContext?.bgmSound && gameStandByBgm) {
        gameStandByBgm.stop();
        configContext.bgmSound.play();
      }
    };
  }, [configContext?.bgmSound, gameStandByBgm]);

  // ----- 落下後リセット -----
  const resetCharToStart = useCallback(() => {
    const g = stateRef.current;
    if (!g?.charMesh) return;
    g.charMesh.position = g.startWorldPos.clone();
    g.charMesh.rotation = new Vector3(0, ROTATION_Y[g.startCharDir], 0);
    g.charDir = g.startCharDir;
  }, []);

  // ----- クリア判定（複数ゴール対応）-----
  // 全ての生きている箱の真下にボタン（CONDITION）があれば clear。
  // 箱が落ちて減った場合はクリア不可。
  const checkClear = useCallback((): boolean => {
    const scene = sceneRef.current;
    const g = stateRef.current;
    if (!scene || !g || scene.isDisposed) return false;

    const boxes = scene.meshes.filter((m) => m.name === "box_asset" && !m.isDisposed());
    if (boxes.length === 0 || boxes.length !== g.totalBoxCount) return false;

    return boxes.every((box) => {
      const ray = new Ray(
        new Vector3(box.position.x, box.position.y, box.position.z),
        new Vector3(0, -1, 0),
        1,
      );
      return !!scene.pickWithRay(ray, isButton)?.hit;
    });
  }, []);

  const tryTriggerClear = useCallback(() => {
    if (success) return;
    if (checkClear()) {
      setSuccess(true);
      levelclearSound();
      levelclearCheersSound();
    }
  }, [success, checkClear, levelclearSound, levelclearCheersSound]);

  // ----- アクション handlers (actions/ hook 経由) -----
  const handleForward = useHandleForward({
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    moveSound,
    failureSound,
    fallingSound,
  });

  const handleJump = useHandleJump({
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    jumpSound,
    failureSound,
    fallingSound,
  });

  const { handleTurnLeft, handleTurnRight } = useHandleTurn({
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    turnSound,
  });

  // ----- リトライ / 次レベル / チュートリアル閉じる -----
  const handleRetry = useCallback(() => {
    navigate(0);
  }, [navigate]);

  const handleNext = useCallback(() => {
    const nextId = id + 1;
    if (nextId <= SOKOBAN_LEVELS.length) {
      navigate(`/courses/sokoban/${nextId}`);
    } else {
      navigate("/courses/sokoban");
    }
  }, [id, navigate]);

  const handleTutorialClose = useCallback(() => {
    setTutorialOpen(false);
    if (stateRef.current) stateRef.current.isAnimating = false;
    setAnimating(false);
  }, []);

  // ----- シーン初期化 -----
  const onSceneReady = useCallback(
    async (canvas: HTMLCanvasElement, engine: Engine, scene: Scene) => {
      if (!map) return;

      const layer1 = map.landscape[0];
      const width = layer1[0].length;
      const height = layer1.length;

      const { camera } = setupGameScene(
        scene,
        canvas,
        {
          alpha: Math.PI / 4,
          beta: Math.PI / 4,
          radius: 22,
          target: { x: width, y: 0, z: height },
          lowerBetaLimit: 0,
          upperBetaLimit: Math.PI / 2,
        },
        { direction: { x: -3, y: 5, z: -3 }, intensity: 1.5 },
      );

      camera.inputs.removeByType("ArcRotateCameraKeyboardMoveInput");

      const { characters } = await loadMap(scene, map);
      const charMesh = characters[0] ?? null;
      charMesh?.setEnabled(true);

      // 箱を box_asset に rename（以降は mesh.name で判別可能）
      scene.meshes.forEach((m) => {
        const meta = m.metadata as { tileFilename?: string } | null | undefined;
        if (meta?.tileFilename === "tileLow_teamRed.gltf.glb") {
          m.name = "box_asset";
        }
      });

      // 衝突アニメ用 mark mesh を読み込み
      const mark = await loadMesh(scene, "lightning.gltf.glb");
      mark.name = "mark";
      mark.setEnabled(false);

      // クリア判定の基準値（dispose で減ったらクリア不可）
      const totalBoxCount = scene.meshes.filter((m) => m.name === "box_asset").length;

      // カメラ初期値（登場演出用）
      const initialCameraState = {
        alpha: camera.alpha,
        beta: camera.beta,
        radius: camera.radius,
        target: camera.target.clone(),
      };

      stateRef.current = {
        charMesh,
        charDir: map.character.direction,
        startWorldPos: charMesh ? charMesh.position.clone() : new Vector3(),
        startCharDir: map.character.direction,
        // 登場演出中はロック（カメラ演出後に解除）
        isAnimating: true,
        totalBoxCount,
      };
      sceneRef.current = scene;

      engine.runRenderLoop(() => scene.render());

      // ----- ゲーム開始時のカメラ登場演出 -----
      if (charMesh) {
        setAnimating(true);
        await performCameraIntro(scene, camera, charMesh.position, initialCameraState);
      }

      // レベル1なら Tutorial を出してロック維持
      if (id === 1) {
        setTutorialOpen(true);
      } else {
        if (stateRef.current) stateRef.current.isAnimating = false;
        setAnimating(false);
      }
    },
    [map, id],
  );

  useCanvas(
    elementRef as React.MutableRefObject<HTMLDivElement>,
    onSceneReady,
    (error) => console.error(error),
    false,
  );

  // ----- キーボード入力 -----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          e.preventDefault();
          handleForward();
          return;
        case "ArrowLeft":
        case "a":
        case "A":
          e.preventDefault();
          handleTurnLeft();
          return;
        case "ArrowRight":
        case "d":
        case "D":
          e.preventDefault();
          handleTurnRight();
          return;
        case " ":
        case "Space":
          e.preventDefault();
          handleJump();
          return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleForward, handleTurnLeft, handleTurnRight, handleJump]);

  if (!isValidId || !map) {
    return <BackToCourseTop />;
  }

  return (
    <div className="flex-col-el flex-center w-full h-full bg-gray2-100 relative">
      <img
        alt="ゲームの背景"
        src={`${Constants.assetHost}/assets/images/bg_scene.jpg`}
        className="absolute top-0 left-0 w-full h-full object-cover pointer-events-none"
      />

      <div ref={elementRef} className="absolute w-full h-full pointer-events-none" />

      <p className="title-text text-textcolor-black type-display absolute top-8 z-10">
        {I18n.t("MSG_COURSE_SOKOBAN_LEVEL")} {id}
      </p>

      <SokobanController
        onForward={handleForward}
        onTurnLeft={handleTurnLeft}
        onTurnRight={handleTurnRight}
        onJump={handleJump}
        disabled={animating || success}
      />

      <SceneJoystick sceneRef={sceneRef} />

      {tutorialOpen && <Tutorial onClose={handleTutorialClose} />}
      {success && <Success onRetry={handleRetry} onNext={handleNext} />}
    </div>
  );
};
